import type { ReactNode } from "react";
import { createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import { useAppSelector } from "./redux-hooks";
import { addressDetails, autocompleteAddress, editAppConfigData, fetchPracticeLookUpByUserRole, fetchPatientLookUpByUserRole, getAllAgenciesList, getAllPayersList, getAncillaryProductManufacturersList, getAppConfigList, getDocumentByDocumentId, getDocumentByDocumentIdPublic, getMacsList, getManufacturersList, getManufacturersListSkipLoader, getPharmaciesListSkipLoader, getPracticeList, getProductList } from "../../service/CommonService";
import { PracticeInfo } from "./practiceSlice";
import { InterceptorOption, LoadingType } from "../../types/CommonTypes";
import { updateElementValue } from "../../utils/updateElementValue";

export type Column = {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

export type MainErrorInfo = {
  title: string;
  errorMessageStream?: string;
  errorMessageList?: string[];
  popupType?: string;
  popupSeverity?: "error" | "success" | "warning" | "info";
  onCloseExt?: () => void;
  buttonName?: string;
  onOkExt?: () => void;
}

export type SingleAlertInfo = {
  title?: string;
  message: string | JSX.Element;
  alertType: "error" | "success" | "warning" | "info";
  //CS-1297 - Start (Display Button inside alert message)
  showButton?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
  buttonIcon?: ReactNode;
  //CS-1297 - ENd
}

export type LoadingDialogConfig = {
  title?: string;
  message?: string;
  variant?: "default" | "aiMedicalAudit";
};

export type MacInfo = {
    macId: number;
    macName: string;
}
export type AppConfigInfo = {
  appconfigId: number;
  configName?: string;
  configValue: string;
}

export type ManufacturerInfo = {
    manufacturerId: number;
    manufacturerName: string;
    contactPhone: string;
    contactEmail: string;
    cartOption?: string;
    //CS-1518
    customConfig?: {
        template_level?: string;
        [key: string]: any;
    };
    //CS-1564 - To identify inactive manufacturers
    inactiveDate?: string | null;
    legalName?: string | null;
    isPharmacy?: boolean;
    agreementGrouping?: string;
    miscConfig?: { billTo?: { enabledValues?: string[]; defaultValue?: string } };
}

export type AgencyInfo = {
  agencyId: string;
  agencyName: string;
  commissionType: string,
  phone: string;
  email: string;
  streetAddress: string;
  unitNumber: string,
  city: string,
  state: string,
  zipCode: string
}

type CommonState = {
  showLoadingDialog: boolean,
  skipLoadingDialog: boolean,
  loadingCount: number,
  loadingDialogConfig: LoadingDialogConfig | null,
  showMainErrorPopup: boolean,
  errorDetailExpand: boolean,
  mainErrorObj: MainErrorInfo | null,
  showSingleAlert: boolean,
  singleAlertObj: SingleAlertInfo | null,
  macInfoList: MacInfo[],
  allPayerList: any,
  documentDataFromCommonApi: any,
  documentDataFromCommonApiPublic: any,
  appConfigList: AppConfigInfo[],
  manufacturerInfoList: ManufacturerInfo[],
  manufacturerInfoListForAncillaryProduct: ManufacturerInfo[],
  pharmacyInfoList: ManufacturerInfo[], // CS-2075
  agencyInfoList: AgencyInfo[],
  practiceInfo?: PracticeInfo[] | null,
  status: "idle" | "loading" | "failed" | "success";
  fetchAgencyListStatus: LoadingType;
  ferchManufactureListStatus: LoadingType;
  ferchManufactureListForAncillaryProductStatus: LoadingType;
  fetchPharmacyListStatus: LoadingType; // CS-2075
  error: string | null;
  isValid: true | false;
  showTrackingLoadingDialog: boolean,
};

const initialState = {
    showLoadingDialog: false,
    skipLoadingDialog: false,
    loadingCount: 0,
    loadingDialogConfig: null,
    showMainErrorPopup: false,
    errorDetailExpand: true,
    mainErrorObj: null,
    showSingleAlert: false,
    singleAlertObj: null,
    macInfoList: [],
    allPayerList:null,
    documentDataFromCommonApi:null,
    documentDataFromCommonApiPublic: null,
    appConfigList: [],
    manufacturerInfoList: [],
    manufacturerInfoListForAncillaryProduct: [],
    pharmacyInfoList: [],
    agencyInfoList: [],
    practiceInfo: [],
    status: "idle",
    fetchAgencyListStatus: "idle",
    ferchManufactureListStatus: "idle",
    ferchManufactureListForAncillaryProductStatus: "idle",
    fetchPharmacyListStatus: "idle",
    error: null,
    isValid: true,
    showTrackingLoadingDialog: false,
} as CommonState;

export const fetchMac = createAsyncThunk("fetchMac", async () => {
    const response = await getMacsList();
    const v3Response = response.data;
    return v3Response.data;
});
export const fetchAllPayers = createAsyncThunk("fetchAllPayers", async () => {
    const response = await getAllPayersList();
    const v3Response = response.data;
    return v3Response.data;
});
export const fetchDocumentByIdCommonApi = createAsyncThunk('fetchDocumentByIdCommonApi',async( documentId: number ) => {
  const response = await getDocumentByDocumentId(documentId);
  const v3Response = response.data;
  return v3Response.data;
});

export const fetchDocumentByIdCommonApiPublic = createAsyncThunk('fetchDocumentByIdCommonApiPublic',async( documentId: number ) => {
  const response = await getDocumentByDocumentIdPublic(documentId);
  const v3Response = response.data;
  return v3Response.data;
});

//cs-433
export const fetchAppConfigList = createAsyncThunk("fetchAppConfigList", async () => {
    const response = await getAppConfigList();
    const v3Response = response.data;
    return v3Response.data;
});

export const editAppConfiguration = createAsyncThunk("editAppConfiguration", async (data: AppConfigInfo, { rejectWithValue }) => {
  try{
  const response = await editAppConfigData(data);
  return response.data;
  }catch(error){
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});
//

// CS-1112 - START
export const autoAddress = createAsyncThunk<any, { addressInput: any, interceptorOption?: InterceptorOption }>(
  "autoAddress",
  async (data, { rejectWithValue }) => {
    const { addressInput, interceptorOption } = data;
    try {
      const response = await autocompleteAddress(addressInput, interceptorOption ,{
      'x-skip-loader': `true`,
    }); 
      return response.data;
    } catch (error) {
      //@ts-ignore
      return rejectWithValue(error?.data);
    }
  }
);


export const fetchAddressDetails = createAsyncThunk<any,{ placeID: any; interceptorOption?: InterceptorOption }>(
  "fetchAddressDetails",async ({ placeID, interceptorOption }, { rejectWithValue }) => {
    try {
      const response = await addressDetails(placeID, interceptorOption);
      return response.data;
    } catch (error) {
      //@ts-ignore
      return rejectWithValue(error?.data);
    }
  }
);

// CS-1112 - END

export const fetchManufacturersList = createAsyncThunk("fetchManufacturersList", async () => {
    const response = await getManufacturersList();
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchManufacturersListSkipLoading = createAsyncThunk<any, {skipLoader?: boolean}>("fetchManufacturersListSkipLoading", async ({skipLoader = false}) => {
    const response = await getManufacturersListSkipLoader({ 'x-skip-loader': skipLoader.toString() });
    const v3Response = response.data;
    return v3Response.data;
});

// CS-2075 — dedicated pharmacy list (manufacturers with isPharmacy = true)
export const fetchPharmaciesList = createAsyncThunk<any, {skipLoader?: boolean}>("fetchPharmaciesList", async ({skipLoader = false}) => {
    const response = await getPharmaciesListSkipLoader({ 'x-skip-loader': skipLoader.toString() });
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchManufacturersListForAncillaryProducts = createAsyncThunk("fetchManufacturersListForAncillaryProducts", async () => {
    const response = await getAncillaryProductManufacturersList();
    const v3Response = response.data;
    return v3Response.data;
});

export const fetchAgenciesList = createAsyncThunk("fetchAgenciesList", async (arg?: { skipLoader?: boolean }) => {
  const response = await getAllAgenciesList(!!arg?.skipLoader);
  const v3Response = response.data;
  return v3Response.data;
});

// CS-1732: Fetch practice lookup by user role for order product modal
export const fetchPracticeLookupByUserRole = createAsyncThunk<any, { activePracticeOnly: boolean; productId?: number; executedOnly?: boolean }>(
  "fetchPracticeLookupByUserRole",
  async ({ activePracticeOnly, productId, executedOnly }) => {
    const response = await fetchPracticeLookUpByUserRole(activePracticeOnly, productId, executedOnly);
    const v3Response = response.data;
    return v3Response.data;
  }
);

// CS-1732: Fetch patient lookup by user role for order product modal
export const fetchPatientLookupByUserRole = createAsyncThunk<any, { activePatientOnly: boolean; practiceId: number }>(
  "fetchPatientLookupByUserRole",
  async ({ activePatientOnly, practiceId }) => {
    const response = await fetchPatientLookUpByUserRole(activePatientOnly, practiceId);
    const v3Response = response.data;
    return v3Response.data;
  }
);

const commonSlice = createSlice({
  name: "commonSlice",
  initialState,
  reducers: {
    showHideLoadingDialog: (
      state: CommonState,
      action: PayloadAction<boolean>
    ) => {
      if (state.skipLoadingDialog && action.payload) {
        return;
      }
      if (action.payload) {
        state.loadingCount += 1;
        state.showLoadingDialog = true;
      } else {
        if (state.loadingCount === 1) {
          state.loadingCount = 0;
          state.showLoadingDialog = false;
        } else if (state.loadingCount > 0){
          state.loadingCount = state.loadingCount - 1;
        }
      }
    },
    setSkipLoadingDialog: (
      state: CommonState,
      action: PayloadAction<boolean>
    ) => {
      state.skipLoadingDialog = action.payload;
      if (action.payload) {
        state.showLoadingDialog = false;
        state.loadingCount = 0;
      }
    },
    setLoadingDialogConfig: (
      state: CommonState,
      action: PayloadAction<LoadingDialogConfig>
    ) => {
      state.loadingDialogConfig = action.payload;
    },
    resetLoadingDialogConfig: (state: CommonState) => {
      state.loadingDialogConfig = null;
    },
    showHideTrackingLoadingDialog: (
      state: CommonState,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload) {
        state.loadingCount += 1;
        state.showTrackingLoadingDialog = true;
      } else {
        if (state.loadingCount === 1) {
          state.loadingCount = 0;
          state.showTrackingLoadingDialog = false;
        } else if (state.loadingCount > 0){
          state.loadingCount = state.loadingCount - 1;
        }
      }
    },
    setMainErrorObj: (state: CommonState, action: PayloadAction<MainErrorInfo>) => {
      state.mainErrorObj = action.payload;
      state.showMainErrorPopup = true;
      state.errorDetailExpand = true;
    },
    hideMainErrorPopup: (state: CommonState) => {
      state.showMainErrorPopup = false;
      state.errorDetailExpand = true;
    },
    setErrorDetailExpand: (state: CommonState, action) => {
      state.errorDetailExpand = action.payload;
    },
    setSingleAlertObj: (state: CommonState, action: PayloadAction<SingleAlertInfo>) => {
      state.singleAlertObj = action.payload;
      state.showSingleAlert = true;
    },
    hideSingleAlert: (state: CommonState) => {
      state.showSingleAlert = false;
    },
    resetDocumentData: (state: CommonState) => {
      state.documentDataFromCommonApi = null;
    },
    updateElementInCommonSlice : updateElementValue,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMac.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchMac.fulfilled, (state, action) => {
        state.status = "success";
        state.isValid = true;
        state.macInfoList = action.payload;
      })
      .addCase(fetchMac.rejected, (state, action) => {
        state.status = "failed";
        state.isValid = false;
        state.error = action.error.message || "Login failed";
      })
      .addCase(fetchAllPayers.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAllPayers.fulfilled, (state, action) => {
        state.status = "success";
        state.allPayerList = action.payload;
      })
      .addCase(fetchAllPayers.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchDocumentByIdCommonApi.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDocumentByIdCommonApi.fulfilled, (state, action) => {
        state.status = "success";
        state.documentDataFromCommonApi = action.payload;
      })
      .addCase(fetchDocumentByIdCommonApi.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchAppConfigList.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchAppConfigList.fulfilled, (state, action) => {
        state.status = "success";
        state.isValid = true;
        state.appConfigList = action.payload;
      })
      .addCase(fetchAppConfigList.rejected, (state, action) => {
        state.status = "failed";
        state.isValid = false;
        state.error = action.error.message || "Login failed";
      })
      .addCase(editAppConfiguration.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editAppConfiguration.fulfilled, (state, action) => {
        state.status = "success";
        // state.appConfigList = action.payload.data;
        state.appConfigList = action.payload?.data; // CS-1984
      })
      .addCase(editAppConfiguration.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? null;
      })
      //CS-1112 - START
      .addCase(autoAddress.pending, (state) => {
        state.status = "loading";
      })
      .addCase(autoAddress.fulfilled, (state, action) => {
        state.status = "success";
      })
      .addCase(autoAddress.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? null;
      })
      .addCase(fetchAddressDetails.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAddressDetails.fulfilled, (state, action) => {
        state.status = "success";
      })
      .addCase(fetchAddressDetails.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? null;
      })
      //CS-1112 - END
      .addCase(fetchManufacturersList.pending, (state) => {
        state.ferchManufactureListStatus = "loading";
        state.error = null;
      })
      .addCase(fetchManufacturersList.fulfilled, (state, action) => {
        state.ferchManufactureListStatus = "success";
        state.isValid = true;
        state.manufacturerInfoList = action.payload;
      })
      .addCase(fetchManufacturersList.rejected, (state, action) => {
        state.ferchManufactureListStatus = "error";
        state.isValid = false;
        state.error = action.error.message || "Login failed";
      })
      .addCase(fetchManufacturersListSkipLoading.pending, (state) => {
        state.ferchManufactureListStatus = "loading";
        state.error = null;
      })
      .addCase(fetchManufacturersListSkipLoading.fulfilled, (state, action) => {
        state.ferchManufactureListStatus = "success";
        state.isValid = true;
        state.manufacturerInfoList = action.payload;
      })
      .addCase(fetchManufacturersListSkipLoading.rejected, (state, action) => {
        state.ferchManufactureListStatus = "error";
        state.isValid = false;
        state.error = action.error.message || "Login failed";
      })
      // CS-2075 — pharmacy list
      .addCase(fetchPharmaciesList.pending, (state) => {
        state.fetchPharmacyListStatus = "loading";
      })
      .addCase(fetchPharmaciesList.fulfilled, (state, action) => {
        state.fetchPharmacyListStatus = "success";
        state.pharmacyInfoList = action.payload ?? [];
      })
      .addCase(fetchPharmaciesList.rejected, (state) => {
        state.fetchPharmacyListStatus = "error";
        state.pharmacyInfoList = [];
      })
      .addCase(fetchManufacturersListForAncillaryProducts.pending, (state) => {
        state.ferchManufactureListForAncillaryProductStatus = "loading";
        state.error = null;
      })
      .addCase(fetchManufacturersListForAncillaryProducts.fulfilled, (state, action) => {
        state.ferchManufactureListForAncillaryProductStatus = "success";
        state.isValid = true;
        state.manufacturerInfoListForAncillaryProduct = action.payload;
      })
      .addCase(fetchManufacturersListForAncillaryProducts.rejected, (state, action) => {
        state.ferchManufactureListForAncillaryProductStatus = "error";
        state.isValid = false;
        state.error = action.error.message || "Login failed";
      })
      .addCase(fetchAgenciesList.pending, (state) => {
        state.fetchAgencyListStatus = "loading";
        state.error = null;
      })
      .addCase(fetchAgenciesList.fulfilled, (state, action) => {
        state.fetchAgencyListStatus = "success";
        state.isValid = true;
        state.agencyInfoList = action.payload;
      })
      .addCase(fetchAgenciesList.rejected, (state, action) => {
        state.fetchAgencyListStatus = "error";
        state.isValid = false;
        state.error = action.error.message || "Login failed";
      })
      .addCase(fetchDocumentByIdCommonApiPublic.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchDocumentByIdCommonApiPublic.fulfilled, (state, action) => {
        state.status = "success";
        state.documentDataFromCommonApiPublic = action.payload;
      })
      .addCase(fetchDocumentByIdCommonApiPublic.rejected, (state, action) => {
        state.status = "failed";
      })
  },
});

export const {
  showHideLoadingDialog,
  setSkipLoadingDialog,
  setLoadingDialogConfig,
  resetLoadingDialogConfig,
  showHideTrackingLoadingDialog,
  setMainErrorObj,
  hideMainErrorPopup,
  setErrorDetailExpand,
  setSingleAlertObj,
  hideSingleAlert,
  updateElementInCommonSlice,
  resetDocumentData
} = commonSlice.actions;
export const useCommonReducer = () => useAppSelector((state) => state.commonSlice);
export default commonSlice.reducer;
